import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ItemResponse } from '@/api/bol';
import {
  formatDateTime,
  formatExternalReferences,
  formatSpecialCharacteristics,
  formatValueWithUnit,
} from '@/utils/datagridFormatters';

interface ItemColumnsProps {
  onDelete: (item: ItemResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createItemColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: ItemColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'serialNumber',
    headerName: 'Serial Number',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { serialNumber?: string }) => row.serialNumber || '-',
  },
  {
    field: 'batchNumber',
    headerName: 'Batch Number',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { batchNumber?: string }) => row.batchNumber || '-',
  },
  {
    field: 'productionDate',
    headerName: 'Production Date',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { productionDate?: string }) => formatDateTime(row.productionDate),
  },
  {
    field: 'productExpiryDate',
    headerName: 'Expiry Date',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { productExpiryDate?: string }) => formatDateTime(row.productExpiryDate),
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { quantity?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.quantity?.value, row.quantity?.unit),
  },
  {
    field: 'weight',
    headerName: 'Weight',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { weight?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.weight?.value, row.weight?.unit),
  },
  {
    field: 'productionCountry',
    headerName: 'Production Country',
    flex: 0.9,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { productionCountry?: { countryCode?: string | null; countryName?: string | null } }) =>
      row.productionCountry?.countryCode || row.productionCountry?.countryName || '-',
  },
  {
    field: 'productsCount',
    headerName: 'Products',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { products?: unknown[] }) => row.products?.length || 0,
  },
  {
    field: 'containedItemsCount',
    headerName: 'Contained Items',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { containedItems?: unknown[] }) => row.containedItems?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (
      _value: unknown,
      row: {
        specialCharacteristics?: Array<{
          specialCharacteristicType?: string | null;
          characteristicReference?: string | null;
          stringValue?: string | null;
          numericalValue?: { value?: number; unit?: string } | null;
        }>;
      }
    ) => formatSpecialCharacteristics(row.specialCharacteristics),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/items/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as ItemResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultItemColumnVisibilityModel = {
  id: true,
  serialNumber: true,
  batchNumber: true,
  productionDate: true,
  productExpiryDate: true,
  quantity: true,
  weight: true,
  productionCountry: true,
  productsCount: false,
  containedItemsCount: false,
  externalReferences: false,
  specialCharacteristics: false,
};
